import { apiClient } from '@/hooks/useAxios';
import {
  CommonRes,
  PutQuestionnaireRequest,
  Questionnaire,
  QuestionnaireListItem,
  QuestionnaireScore,
} from '@/types';

const ApiPath = '/questionnaire';

export const createQuestionnaire = async (): Promise<
  CommonRes<{ id: string }>
> => {
  const res = await apiClient.post<CommonRes<{ id: string }>>(`${ApiPath}`);
  return res.data;
};

export const fetchQuestionnaire = async (
  questionnaireId: string,
  showLoading: boolean = true,
): Promise<CommonRes<Questionnaire>> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const res = await apiClient.get<CommonRes<Questionnaire>>(
    `${ApiPath}/${questionnaireId}`,
    config,
  );
  return res.data;
};

export const fetchQuestionnaires = async (
  showLoading: boolean = true,
): Promise<CommonRes<QuestionnaireListItem[]>> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const res = await apiClient.get<CommonRes<QuestionnaireListItem[]>>(
    `${ApiPath}`,
    config,
  );
  return res.data;
};

export const putQuestionnaire = async (
  data: PutQuestionnaireRequest,
  showLoading: boolean = true,
): Promise<
  CommonRes<
    Questionnaire | { updated: boolean; clearedChildQuestionIds?: string[] }
  >
> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const { questionnaireId, ...bodyData } = data;
  const res = await apiClient.put<
    CommonRes<
      Questionnaire | { updated: boolean; clearedChildQuestionIds?: string[] }
    >
  >(`${ApiPath}/${questionnaireId}`, bodyData, config);

  return res.data;
};

export const deleteQuestionnaire = async (
  questionnaireId: string,
  showLoading: boolean = true,
): Promise<CommonRes<{ deleted: string }>> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const res = await apiClient.delete<CommonRes<{ deleted: string }>>(
    `${ApiPath}/${questionnaireId}`,
    config,
  );
  return res.data;
};

export const submitQuestionnaire = async (
  questionnaireId: string,
  showLoading: boolean = true,
): Promise<CommonRes<{ submitted: string }>> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const res = await apiClient.post<CommonRes<{ submitted: string }>>(
    `${ApiPath}/submit`,
    { questionnaireId },
    config,
  );
  return res.data;
};

export const cancelAiGeneration = async (
  questionnaireId: string,
  showLoading: boolean = false,
): Promise<CommonRes<{ cancelled: boolean }>> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const res = await apiClient.post<CommonRes<{ cancelled: boolean }>>(
    `${ApiPath}/cancel-generation`,
    { questionnaireId },
    config,
  );
  return res.data;
};

export const getQuestionnaireScore = async (
  questionnaireId: string,
  showLoading: boolean = true,
): Promise<CommonRes<QuestionnaireScore>> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const res = await apiClient.get<CommonRes<QuestionnaireScore>>(
    `/result/${questionnaireId}`,
    config,
  );
  return res.data;
};

export const recalculateQuestionnaireScore = async (
  questionnaireId: string,
  showLoading: boolean = true,
): Promise<CommonRes<QuestionnaireScore>> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const res = await apiClient.post<CommonRes<QuestionnaireScore>>(
    `${ApiPath}/${questionnaireId}/score/recalculate`,
    {},
    config,
  );
  return res.data;
};

export const updateResultAiSummary = async (
  questionnaireId: string,
  aiSummary: string,
  showLoading: boolean = true,
): Promise<CommonRes<{ updated: boolean }>> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const res = await apiClient.put<CommonRes<{ updated: boolean }>>(
    `${ApiPath}/${questionnaireId}/result/ai-summary`,
    { aiSummary },
    config,
  );
  return res.data;
};

export const updateSdgAiDescription = async (
  questionnaireId: string,
  sdgGoalId: string,
  aiDescription: string,
  showLoading: boolean = true,
): Promise<CommonRes<{ updated: boolean }>> => {
  const config = showLoading ? {} : { headers: { 'X-Show-Loading': 'false' } };
  const res = await apiClient.put<CommonRes<{ updated: boolean }>>(
    `${ApiPath}/${questionnaireId}/result/sdg/${sdgGoalId}/ai-description`,
    { aiDescription },
    config,
  );
  return res.data;
};
